import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  inject
} from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { IManagableObject } from "../../../admin-panel/IManagableObject";
import { AccessRights, ApplicationUserStatisticsView, UsersService } from "app/services/users.service";
import { DialogService } from "app/services/dialog.service";
import { ObjectsManagementDialogComponent } from "../../../admin-panel/objects-management-dialog/objects-management-dialog.component";
import { ToastrService } from "ngx-toastr";
import { CompaniesService, CompanyModel } from "app/services/companies.service";
import { ContainersService } from "app/services/containers.service";
import { ContainerViewModel } from "../../../admin-panel/ContainerViewModel";
import { ContainerUserLink } from "app/models/ContainerUserLink";
import { ContainerAcl } from "app/models/ContainerAcl";
import { CONTAINER_ACL_GROUPS, getContainerAclPresets, getContainerFullAccessMask } from "../../model/container-acl";
import { matchesAnySearchRule } from "../../pipes/filterByProperty";
import { forkJoin } from 'rxjs';

interface ContainerUserItem {
  userId: number;
  name: string;
  email: string;
  acl: number;
  companyId: number;
}

interface ContainerAccessUser extends IManagableObject {
  Email: string;
  CompanyId: number;
  Acl: number;
}

enum ContainerDialogMode {
  Create = "create",
  Edit = "edit"
}

type ContainerDialogTarget = IManagableObject | ContainerViewModel | null | undefined;

@Component({
  selector: 'app-container-access',
  templateUrl: './container-access.component.html',
  styleUrls: ['./container-access.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContainerAccessComponent implements AfterViewInit, OnInit {
  private translate = inject(TranslateService);
  private usersService = inject(UsersService);
  private dialogService = inject(DialogService);
  private toastr = inject(ToastrService);
  private companiesService = inject(CompaniesService);
  private containersService = inject(ContainersService);
  private cdr = inject(ChangeDetectorRef);

  @Input()
  public isEditable: boolean = true;

  @Input()
  public companyId: number = 0;

  @Input()
  public companyName: string = "";

  @Output()
  public containersChanged: EventEmitter<void> = new EventEmitter<void>();

  public dialogId: string = "container-access-dialog";
  public isDialogVisible: boolean = false;
  public dialogMode: ContainerDialogMode = ContainerDialogMode.Create;
  public draftContainerName: string = "";
  public containerUsers: ContainerUserItem[] = [];
  public selectedUser: ContainerUserItem | null = null;
  public searchTerm: string = "";
  public activePresetMask: number | null = null;
  public nameTouched: boolean = false;
  public isDialogDataLoading: boolean = true;

  public readonly aclGroups = CONTAINER_ACL_GROUPS;
  public readonly aclPresets = getContainerAclPresets();
  public readonly DialogMode = ContainerDialogMode;

  private currentContainer: ContainerViewModel | null = null;
  private users: ContainerAccessUser[] = [];
  private usersById: Map<number, ContainerAccessUser> = new Map();
  private companies: CompanyModel[] = [];
  public currentUserId: number = 0;
  private isLoadingDetails: boolean = false;

  private readonly userSort = (a: ContainerUserItem, b: ContainerUserItem) => {
    if (a.userId === this.currentUserId && b.userId !== this.currentUserId) return -1;
    if (b.userId === this.currentUserId && a.userId !== this.currentUserId) return 1;
    return (a.name || "").localeCompare(b.name || "");
  };

  @ViewChild("usersListDialog", { read: ViewContainerRef, static: false }) usersListContainer!: ViewContainerRef;
  private usersListDialog!: ComponentRef<ObjectsManagementDialogComponent>;

  ngOnInit() {
    this.currentUserId = this.usersService.getCurrentUser()?.Id ?? 0;
    this.isDialogDataLoading = true;

    forkJoin({
      users: this.usersService.getUsersStatistics(),
      companies: this.companiesService.getCompanies()
    }).subscribe(result => {
      this.users = result.users.map(user => this.toAccessUser(user));
      this.usersById = new Map(this.users.map(user => [user.Id, user]));
      this.rehydrateUsers();
      this.companies = result.companies ?? [];
      this.isDialogDataLoading = false;
      this.cdr.markForCheck();
    }, () => {
      this.isDialogDataLoading = false;
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit() {
    document.dispatchEvent(new CustomEvent("readyAgain"));
  }

  public openDialog(container?: ContainerDialogTarget) {
    this.isDialogVisible = true;
    this.resetDialogState();

    const containerId = this.getContainerId(container);
    if (containerId > 0) {
      this.dialogMode = ContainerDialogMode.Edit;
      this.currentContainer = this.asContainer(container) ?? this.createContainerPlaceholder(containerId, container);
      this.loadContainerDetails(containerId);
    } else {
      this.dialogMode = ContainerDialogMode.Create;
      this.prepareCreateMode();
    }

    this.cdr.markForCheck();
  }

  public closeDialog() {
    this.isDialogVisible = false;
    this.resetDialogState();
    this.cdr.markForCheck();
  }

  public get filteredUsers(): ContainerUserItem[] {
    const term = (this.searchTerm || "").trim();
    if (!term) return this.containerUsers;

    return this.containerUsers.filter(user =>
      matchesAnySearchRule(term, user.name, user.email)
    );
  }

  public get resolvedCompanyName(): string {
    if (this.companyName) return this.companyName;
    const companyId = this.companyId || this.currentContainer?.CompanyId || 0;
    return this.companies.find(company => company.Id === companyId)?.Name ?? "";
  }

  public canEditContainerName(): boolean {
    if (!this.isEditable) return false;
    return this.isAdminEditor();
  }

  public canManageMembers(): boolean {
    if (!this.isEditable) return false;
    return this.isAdminEditor();
  }

  public canSave(): boolean {
    if (!this.isEditable) return false;
    return this.isAdminEditor();
  }

  public get isLoading(): boolean {
    return this.isDialogDataLoading || this.isLoadingDetails;
  }

  public canEditUserAcl(item: ContainerUserItem | null): boolean {
    return !!item && this.canManageMembers() && item.userId !== this.currentUserId;
  }

  public canRemoveUser(item: ContainerUserItem | null): boolean {
    return !!item && this.canManageMembers() && item.userId !== this.currentUserId;
  }

  public selectUser(item: ContainerUserItem) {
    this.selectedUser = item;
    this.activePresetMask = this.enforceAclDependencies(item.acl);
  }

  public isAclSet(item: ContainerUserItem, acl: number): boolean {
    const effectiveAcl = this.enforceAclDependencies(item.acl);
    return (effectiveAcl & acl) === acl;
  }

  public isAclAllowed(acl: number): boolean {
    const maxMask = this.getMaxAclMask();
    return (maxMask & acl) === acl;
  }

  public onAclChange(item: ContainerUserItem, acl: number, event: Event) {
    if (!this.canEditUserAcl(item)) return;

    const input = event.target as HTMLInputElement;
    let next = input.checked ? (item.acl | acl) : (item.acl & ~acl);

    if (!input.checked && acl === ContainerAcl.EditGroup) {
      next &= ~ContainerAcl.ManageMembers;
    }

    item.acl = this.enforceAclDependencies(next & this.getMaxAclMask());
    this.activePresetMask = item.acl;
    this.syncContainerUsers();
    this.cdr.markForCheck();
  }

  public applyPreset(mask: number) {
    if (!this.canEditUserAcl(this.selectedUser)) return;

    this.selectedUser!.acl = this.enforceAclDependencies(mask) & this.getMaxAclMask();
    this.selectedUser!.acl = this.enforceAclDependencies(this.selectedUser!.acl);
    this.activePresetMask = this.selectedUser!.acl;
    this.syncContainerUsers();
    this.cdr.markForCheck();
  }

  public openUsersPicker() {
    if (!this.canManageMembers()) return;

    const companyLookup = this.buildCompanyLookup();
    const assignedIds = new Set(this.containerUsers.map(user => user.userId));
    const containerId = this.currentContainer?.Id ?? 0;
    const targetCompanyId = this.currentContainer?.CompanyId || this.companyId || 0;
    this.usersListDialog = this.dialogService.open(this.usersListContainer, ObjectsManagementDialogComponent);
    this.usersListDialog.instance.onOpen.subscribe(() => {
      this.usersListDialog.instance.groups = [];
      this.usersListDialog.instance.title = this.translate.instant("Users list");
      this.usersListDialog.instance.companyLookup = companyLookup;
      this.usersListDialog.instance.showCompanyFilterAndColumn = true;
      this.usersListDialog.instance.objects = [];
      this.usersListDialog.instance.isLoading = true;

      const usersRequest = containerId > 0
        ? this.usersService.getUsersForContainer(containerId)
        : this.usersService.getUsersForNewContainer(targetCompanyId);

      usersRequest.subscribe({
        next: users => {
          this.usersListDialog.instance.objects = (users ?? [])
            .map(user => {
              const candidate = this.toAccessUser(user);
              candidate.CanSelect = candidate.CanSelect !== false && !assignedIds.has(candidate.Id);
              return candidate;
            })
            .sort((a, b) => Number(b.CanSelect) - Number(a.CanSelect));
          this.usersListDialog.instance.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.usersListDialog.instance.objects = [];
          this.usersListDialog.instance.isLoading = false;
          this.cdr.markForCheck();
        }
      });
    });

    this.usersListDialog.instance.onSubmit.subscribe(() => {
      const selectedIds = (this.usersListDialog.instance.objects || [])
        .filter((user: ContainerAccessUser) => user && user.IsChecked)
        .map((user: ContainerAccessUser) => user.Id);
      this.addUsers(selectedIds);
      this.usersListDialog.instance.close();
      this.cdr.markForCheck();
    });

    this.usersListDialog.instance.open(null);
  }

  public removeUser(item: ContainerUserItem, event?: Event) {
    if (event) event.stopPropagation();
    if (!this.canRemoveUser(item)) return;

    const message = this.translate.instant("Delete");
    if (!confirm(message + " " + item.name + "?")) return;

    this.containerUsers = this.containerUsers.filter(user => user.userId !== item.userId);
    if (this.selectedUser?.userId === item.userId) {
      this.selectedUser = this.containerUsers[0] ?? null;
      this.activePresetMask = this.selectedUser ? this.enforceAclDependencies(this.selectedUser.acl) : null;
    }
    this.syncContainerUsers();
    this.cdr.markForCheck();
  }

  public saveDialog() {
    this.nameTouched = true;
    const name = (this.draftContainerName || "").trim();
    if (!name) {
      this.cdr.markForCheck();
      return;
    }

    const users = this.containerUsers.map(user => ({
      UserId: user.userId,
      ACL: user.acl
    }));

    if (this.dialogMode === ContainerDialogMode.Create) {
      this.containersService.CreateContainer({
        Name: name,
        CompanyId: this.companyId,
        Users: users
      }).subscribe(() => {
        this.toastr.success(this.translate.instant("Container created"));
        this.containersChanged.emit();
        this.closeDialog();
      });
      return;
    }

    if (!this.currentContainer?.Id) return;

    this.containersService.UpdateContainer({
      Id: this.currentContainer.Id,
      Name: name,
      CompanyId: this.currentContainer.CompanyId || this.companyId,
      Users: users
    }).subscribe(() => {
      this.toastr.success(this.translate.instant("Container updated"));
      this.containersChanged.emit();
      this.closeDialog();
    });
  }

  public getCompanyName(companyId: number): string {
    return this.companies.find(company => company.Id === companyId)?.Name ?? "";
  }

  private prepareCreateMode() {
    this.currentContainer = new ContainerViewModel();
    this.currentContainer.CompanyId = this.companyId;
    this.draftContainerName = "";
  }

  private loadContainerDetails(containerId: number) {
    this.isLoadingDetails = true;
    this.containersService.GetContainer(containerId).subscribe({
      next: container => {
        this.currentContainer = container;
        this.draftContainerName = container?.Name ?? "";
        this.containerUsers = this.sortUserItems(
          (container?.Users ?? []).map(link => this.toContainerUserItem(link))
        );
        this.selectedUser = this.containerUsers[0] ?? null;
        this.activePresetMask = this.selectedUser ? this.enforceAclDependencies(this.selectedUser.acl) : null;
        this.isLoadingDetails = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoadingDetails = false;
        this.toastr.error(this.translate.instant("Failed to load container"));
        this.closeDialog();
      }
    });
  }

  private addUsers(userIds: number[]) {
    const normalizedIds = [...new Set((userIds ?? []).filter(userId => userId > 0))];
    if (normalizedIds.length === 0) return;

    normalizedIds.forEach(userId => {
      if (this.containerUsers.some(user => user.userId === userId)) return;
      this.containerUsers.push(this.createUserItem(userId, 0));
    });

    this.containerUsers = this.sortUserItems(this.containerUsers);
    if (!this.selectedUser) {
      this.selectedUser = this.containerUsers[0] ?? null;
    } else {
      this.selectedUser = this.containerUsers.find(user => user.userId === this.selectedUser?.userId) ?? this.selectedUser;
    }
    this.activePresetMask = this.selectedUser ? this.enforceAclDependencies(this.selectedUser.acl) : null;
    this.syncContainerUsers();
  }

  private toContainerUserItem(link: ContainerUserLink): ContainerUserItem {
    const user = this.usersById.get(link.UserId);

    return {
      userId: link.UserId,
      acl: link.ACL,
      name: user?.Name ?? "",
      email: user?.Email ?? "",
      companyId: user?.CompanyId ?? 0
    };
  }

  private rehydrateUsers() {
    if (!this.containerUsers.length) return;

    this.containerUsers = this.sortUserItems(this.containerUsers.map(user => this.createUserItem(user.userId, user.acl)));
    if (this.selectedUser) {
      this.selectedUser = this.containerUsers.find(user => user.userId === this.selectedUser?.userId) ?? this.containerUsers[0] ?? null;
      this.activePresetMask = this.selectedUser ? this.enforceAclDependencies(this.selectedUser.acl) : null;
    }
    this.syncContainerUsers();
  }

  private syncContainerUsers() {
    if (!this.currentContainer) return;

    this.currentContainer.Name = (this.draftContainerName || "").trim();
    this.currentContainer.Users = this.containerUsers.map(user => ({
      UserId: user.userId,
      ACL: user.acl
    }));
  }

  private normalizeAcl(acl: number): number {
    if ((acl & ContainerAcl.ManageMembers) === ContainerAcl.ManageMembers) {
      acl |= ContainerAcl.EditGroup;
    }
    if ((acl & (ContainerAcl.CreateGeozone | ContainerAcl.EditGeozone | ContainerAcl.DeleteGeozone)) !== 0) {
      acl |= ContainerAcl.ReadGeozone;
    }
    if ((acl & (ContainerAcl.CreateDriver | ContainerAcl.EditDriver | ContainerAcl.DeleteDriver)) !== 0) {
      acl |= ContainerAcl.ReadDriver;
    }
    if ((acl & (ContainerAcl.CreateDriverKey | ContainerAcl.EditDriverKey | ContainerAcl.DeleteDriverKey)) !== 0) {
      acl |= ContainerAcl.ReadDriverKey;
    }
    if ((acl & (ContainerAcl.CreateReportTemplate | ContainerAcl.EditReportTemplate | ContainerAcl.DeleteReportTemplate)) !== 0) {
      acl |= ContainerAcl.ReadReportTemplate;
    }
    return acl;
  }

  private enforceAclDependencies(acl: number): number {
    let next = this.normalizeAcl(acl);
    if ((next & ContainerAcl.EditGroup) !== ContainerAcl.EditGroup) {
      next &= ~ContainerAcl.ManageMembers;
    }
    return next;
  }

  private getMaxAclMask(): number {
    if (this.isAdminEditor()) {
      return getContainerFullAccessMask();
    }

    if (this.dialogMode === ContainerDialogMode.Create) {
      return this.containerUsers.find(user => user.userId === this.currentUserId)?.acl ?? 0;
    }

    return this.getCurrentUserAcl();
  }

  private getCurrentUserAcl(): number {
    const currentFromUsers = this.containerUsers.find(user => user.userId === this.currentUserId)?.acl;
    if (currentFromUsers !== undefined) {
      return currentFromUsers;
    }

    const currentLink = this.currentContainer?.Users?.find(link => link.UserId === this.currentUserId);
    if (currentLink) {
      return currentLink.ACL;
    }

    return this.usersService.getCurrentUser()?.ContainerLinks?.find(link => link.ContainerId === this.currentContainer?.Id)?.ACL ?? 0;
  }

  private isAdminEditor(): boolean {
    return this.usersService.IsPermitted(AccessRights.EDITUSERS)
      || this.usersService.IsPermitted(AccessRights.EDIT_ALL_USERS);
  }

  private createUserItem(userId: number, acl: number): ContainerUserItem {
    const user = this.usersById.get(userId);
    return {
      userId,
      name: user?.Name ?? "",
      email: user?.Email ?? "",
      acl,
      companyId: user?.CompanyId ?? 0
    };
  }

  private sortUserItems(users: ContainerUserItem[]): ContainerUserItem[] {
    return [...users].sort(this.userSort);
  }

  private buildCompanyLookup(): { [key: number]: string } | null {
    if (!this.companies?.length) return null;
    const lookup: { [key: number]: string } = {};
    this.companies.forEach(company => {
      lookup[company.Id] = company.Name;
    });
    return lookup;
  }

  private toAccessUser(user: ApplicationUserStatisticsView): ContainerAccessUser {
    return {
      Id: user.Id,
      Name: user.Name ?? "",
      Email: user.Email ?? "",
      CompanyId: user.CompanyId ?? 0,
      Acl: user.Acl ?? 0,
      IsChecked: false,
      CanSelect: user.CanSelect
    };
  }

  private resetDialogState() {
    this.draftContainerName = "";
    this.containerUsers = [];
    this.selectedUser = null;
    this.searchTerm = "";
    this.activePresetMask = null;
    this.nameTouched = false;
    this.currentContainer = null;
  }

  private getContainerId(container?: ContainerDialogTarget): number {
    return container?.Id && container.Id > 0 ? container.Id : 0;
  }

  private asContainer(container?: ContainerDialogTarget): ContainerViewModel | null {
    return this.isContainerViewModel(container) ? container : null;
  }

  private createContainerPlaceholder(containerId: number, container?: ContainerDialogTarget): ContainerViewModel {
    const placeholder = new ContainerViewModel();
    placeholder.Id = containerId;
    placeholder.CompanyId = this.companyId;
    if (container) {
      placeholder.Name = container.Name;
    }
    return placeholder;
  }

  private isContainerViewModel(container?: ContainerDialogTarget): container is ContainerViewModel {
    return !!container && "CompanyId" in container;
  }
}
